import React from 'react'

import MonoalphabetWidget from '../components/MonoalphabetWidget'
import {default as MonoalphabetBreakWidget, } from '../components/MonoalphabetBreakWidget/index'
import { LetterFreques } from '../components/MonoalphabetBreakWidget/LetterFrequencies'
import {KEY_2, encipher} from '../ciphers/monoalphabet'
import { monoalphabetText } from './cipherTexts'
import KaterineDialog from '../components/KaterineDialog'
import { Chapter2Text as Chapter2LetterFreques } from '../content/letterFrequency'
import NextChapterLink from '../components/NextChapterLink'
import NavigationContainer from '../components/NavigationContainer/index'
import NewTabLink from '../components/NewTabLink'

import MonoInstruction from '../img/mono_widget_instruction.gif'


export default function ChapterTwo() {
  return (
<NavigationContainer linkBack='/' linkForward='/3' level={2}>
<article>
  <h2>Шифри одноалфавітної заміни</h2>
  <p className="main-text">
    Шифр Цезаря&nbsp;— це один із прикладів шифрів <em>одноалфавітної заміни</em>.
    Усі вони працюють за однаковим принципом&nbsp;— у відкритому тексті літери одного,
    нормального, алфавіту замінюються на відповідники з іншого, зміненого.
    Шифр Цезаря зручний тим, що новий секретний алфавіт у ньому будується на основі простого правила,
    а ключ короткий і його легко запам’ятати, бо це одне невелике число.
    А, в разі чого, і підібрати можна, що вже, звісно, погано.
  </p>
  <p className="main-text">
    Багато одноалфавітних шифрів не мають ніякого правила, як робити заміни.
    Ключем є просто таблиця, у якій кожній букві відповідає який-небудь символ,
    що має використовуватися у шифротексті замість неї.
    Наприклад, для української абетки можна вибрати 33 символи і попарно протиставити їх 33 буквам.
    Існує <span className='msg-quote'>8683317618811886495518194401280000000</span> різних
    способів скласти такі пари.
    Щоби підібрати ключа простим перебором, не вистачило б і часу, що пройшов від початку Всесвіту.
    Тому, на перший погляд, здається, що такий шифр вже точно мав би бути надійним.
  </p>

  <div className="command">
    <p className="command__phrase">
      Спробуй зашифрувати чи розшифрувати власні повідомлення. Для цього введи текст у відповідне поле.
    </p>
    <MonoalphabetWidget/>
  </div>

  <p className="main-text">
    У Європі 16 століття з її інтригами, секретними домовленостями,
    стратегічними шлюбами, змовами, вбивствами, зрадами й іншими такими справами,
    одноалфавітні шифри були дуже популярними. Наприклад, в Англії однією з перших їх стала використовувати
    Катерина Арагонська. Вона була дочкою іспанських короля і королеви Фердинанда II та Ізабелли I.
    Коли їй виповнилося 16, Катерину відіслали до Англії,
    де вона вийшла заміж за 14-річного королівського сина, але той наступного ж року помер.
    Молода вдова залишилася сама на чужині і ще шість років чекала на весілля з наступним принцом,
    та воно постійно відкладалося.
  </p>
  <p className="main-text">
    У цей час вона самостійно вивчила шифр, який використовував іспанський посол для спілкування з її батьком,
    а згодом придумала власний. Її знання криптографії сприяло тому, що у 1507 році Катерина сама зайняла
    дипломатичну посаду і стала першою жінкою послом в історії Європи.

    Ще за два роки вона дочекалася шлюбу зі своїм нареченим, який зійшов на престол як король
    Генріх VIII.
    Королева Катерина продовжувала використовувати свій шифр, щоби бути таємною посередницею у спілкуванні
    між двома монархами&nbsp;— своїм батьком і чоловіком.
    Листи Катерини до Фердинанда II, між іншим, так і не розшифрували до 2018 року.
    Але причиною цього міг бути брак уваги зі сторони дослідників, а не складність її шифру.
  </p>
  <div className="command">
    <p className="command__phrase">
      Репліки Катерини і її батька на картинці можна виділити і скопіювати.
      Здогадуєшся, як їх розшифрувати?
    </p>
    <KaterineDialog />
    <p className="media-attribution">
      Зображення створено на основі портретів <NewTabLink
        href="https://commons.wikimedia.org/wiki/File:Katharine_of_Aragon_with_a_monkey.jpg?uselang=uk">
        Катерини Арагонської
      </NewTabLink> і <NewTabLink href="https://commons.wikimedia.org/wiki/File:Michel_Sittow_004.jpg?uselang=uk">
        Фердинанда II
      </NewTabLink>, обидві роботи є <NewTabLink href="https://uk.wikipedia.org/wiki/Суспільне_надбання?uselang=uk">
        суспільним надбанням
      </NewTabLink>
    </p>
  </div>
  <p className="main-text">
    Подивімося, як можна було би підійти до прочитання цих листів, якби вони потрапили тобі до рук.
    Тут знадобиться якийсь розумніший метод, ніж просто пробувати всі можливі варіанти.
    Добре було би знайти зачіпку&nbsp;— якусь закономірність у використанні букв,
    що підкаже шлях до злому шифру.
  </p>
  <p className="main-text">
    Про одну таку закономірність знали арабські дослідники вже у 9 столітті&nbsp;— майже за 700 років до того,
    як Катерина Арагонська писала свої листи.
    Ці вчені надзвичайно серйозно ставилися до вивчення Корану, священної книги мусульман.
    Вони не лише трактували зміст сакрального тексту, а й просто підраховували,
    яка буква скільки разів там трапляється. Виявилося, що деякі літери у Корані дуже поширені,
    а деякі, навпаки, вкрай рідкісні. Глянувши на інші тексти арабською,
    дослідники зрозуміли, що розподіл частоти букв завжди зберігається більш-менш однаковим.
    Один із вчених, полімат Аль-Кінді, здогадався використати цей факт,
    аби знаходити ключі до зашифрованих повідомлень.
    Він описав свій метод, який ми звемо <em>частотним криптоаналізом</em>,
    у книжці «Манускрипт про дешифрування криптографічних листів».
  </p>
  <p className="main-text">
    Для частотного аналізу слід знати, якою мовою написане повідомлення.
    Взяти досить великий зразок тексту цією мовою і підрахувати у ньому всі букви.
    Далі зробити те ж із зашифрованим повідомленням.
    Маючи списки частоти символів в обидвох текстах, можна почати заміняти
    найпоширеніші символи із шифротексту на найпоширеніші букви мови.
    Це рідко дає цілком правильний результат відразу,
    але частина букв стануть на свої місця, і в шифротексті почнуть впізнаватися деякі слова,
    які можна використати, щоби виправляти помилки.
  </p>
  <p className="main-text">
    Спробуймо зламати одноалфавітний шифр українською мовою. Сам зашифрований текст&nbsp;— у
    кінці сторінки, а ми почнемо з оцінки частоти букв в українській мові.
    Граною ідеєю буде використати як зразок текст цього розділу.
    Адже, якщо є така можливість, то найліпше вибирати щось, написане тим же автором
    на ту ж тему, що й зашифроване повідомлення.
    Щоб тобі не довелося годинами сліпати над підрахунком букв&nbsp;— ось вже готова таблиця.
  </p>
  <div className="command">
    <div className='cipher-widget'>
      <h3 className="cipher-widget__title">Частота букв у тексті цього розділу</h3>
      <div className='cipher-widget__body'>
      <LetterFreques lettersInfo={Chapter2LetterFreques} isPlainText />
      </div>
    </div>
  </div>
  <div className="main-text">
    У підрахунку ми опустили всі символи, які не треба буде розшифровувати&nbsp;— розділові знаки, пробіли і
    таке інше. Стовпці над кожною літерою показують, скільки відсотків тексту вона займає. Ти можеш навести
    мишкою на стовпець і побачити конкретну цифру, але нас більше цікавлять загальні закономірності. Наприклад,
    видно, що найпоширеніші&nbsp;— три голосні (О, А, И), вони не сильно відрізняються між собою за частотою, але
    мають суттєвий відрив від наступної приголосної&nbsp;— Т. Це спостереження може бути зачіпкою для початку злому
    шифротексту нижче. Вперед до наступного рівня криптоаналітичних вмінь!

  </div>
  <div className="command">
    <p className="command__phrase">
      Спробуй зламати цей шифр. Верхній (зелений) рядок показує частоту символів у шифротексті,
      ти можеш клацати мишкою на будь-яку з букв, щоб побачити, де саме в тексті вона
      зустрічається. Нижній (блакитний) рядок показує поширення символів у тексті-зразку.
      Букви цього блоку можна перетягувати мишкою з місця на місце.
      <img src={MonoInstruction} width={600} alt="gif інструкція використання віджета злому"/>
      <br/>Наприклад, якщо ти підозрюєш,
      що за $ у шифротексті ховається літера Б, знайди Б у нижньому рядку (для цього можна використати
      поле пошуку зліва), перетягни її під символ $ і клацни на кружечок між ними. Всі $ у
      шифротексті зміняться на Б. Якщо ти передумаєш і вирішиш, що парою до $ є, скажімо, А,
      слід спершу «відчепити» Б, клацнувши на смужці, яка сполучає її з верхнім рядком, а тоді
      вже перетягувати А на це місце.

      Якщо ти раптом застрягнеш і не знатимеш, що роботи, можеш скористатися підказками.
    </p>
    <MonoalphabetBreakWidget text={encipher(monoalphabetText, KEY_2)}/>
  </div>
</article>
<NextChapterLink link='/3' keyword='поліалфавіт' level={2}/>
</NavigationContainer>)
}